import { defaultsDeep, isObject, isUndefined, isString, snakeCase, isFunction, startCase, isEqual, pick } from 'lodash-es';
import { ifElse, view, lensPath, identity, map, cond, when, T, tryCatch, curry, mergeDeepRight } from 'ramda';

var RECEIVE = 'received';
var PAGE = 'viewed';
var VIEW = 'viewed';
var CHANGE = 'changed';
var CLICK = 'clicked';
var SELECT = 'selected';
var CLOSE = 'closed';
var SUBMIT = 'submitted';
var DISPLAY = 'displayed';
var TRACK = 'tracked';
var UPDATE = 'updated';
var MEASURE = 'measured';
var BOUNCE = 'bounced';
var ERROR = 'errored';
var WARNING = 'warned';
var PURCHASE = 'purchased';
var DECLINE = 'declined';
var ENROLL = 'enrolled';
var UPLOAD = 'uploaded';
var ENGAGE = 'engaged';

// These are legacy verbs that we'd like to avoid
// using. For that reason, we have not documented them.
var TRY = 'tried';
var VERIFY = 'verified';
var CREATE = 'created';
var SAVE = 'saved';
var CONFIRM = 'confirmed';
var CANCEL = 'canceled';
var DISMISS = 'dismissed';
var ENTER = 'entered';
var LOAD = 'loaded';
var VALIDATE = 'validated';
var SENT = 'sent';
var RESENT = 'resent';
var ADD = 'added';
var REMOVE = 'removed';
var OPEN = 'opened';

var VERBS = [RECEIVE, PAGE, VIEW, CHANGE, CLICK, SELECT, CLOSE, SUBMIT, DISPLAY, TRACK, UPDATE, MEASURE, BOUNCE, ERROR, WARNING, TRY, VERIFY, CREATE, SAVE, CONFIRM, CANCEL, DISMISS, ENTER, LOAD, VALIDATE, SENT, RESENT, ADD, REMOVE, OPEN, DECLINE, PURCHASE, ENROLL, UPLOAD, ENGAGE];

var TRACKED_EVENTS = {
  change: {
    verb: CHANGE,
    detail: true
  },
  click: {
    verb: CLICK,
    detail: false
  },
  close: {
    verb: CLOSE,
    detail: false
  },
  submit: {
    verb: SUBMIT,
    detail: true
  }
};

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

/**
 * @memberof module:EventTracker
 * @private
 *
 * @param dataset An element's `dataset` property.
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dataset
 * @return {Object} A nested object representation of `dataset` (DOMStringMap).
 *
 * @example
 * ```html
 * <div
 *   id="myElement"
 *   data-analytics-event="My Event"
 *   data-analytics-properties-value="My Event Value"
 * >
 * ```
 * ```js
 * const { dataset } = document.querySelector('#myElement');
 * const datasetObj = datasetToObject(dataset);
 *
 * console.log(dataset.analyticsEvent, dataset.analyticsPropertiesValue);
 * // --> "My Event", "My Event Value"
 *
 * console.log(datasetObj.analytics.event, datasetObj.analytics.properties.value);
 * // --> "My Event", "My Event Value"
 * ```
 */
var datasetToObject = function datasetToObject(dataset) {
  return defaultsDeep.apply(undefined, [{}].concat(_toConsumableArray(Object.keys(dataset).map(function (key) {
    return key.replace(/([A-Z])/g, function (_, match) {
      return ' ' + match.toLowerCase();
    }).split(' ').reduceRight(function (obj, prop) {
      return _defineProperty({}, prop, obj);
    }, tryCatch(function () {
      return JSON.parse(dataset[key]);
    }, function () {
      return dataset[key];
    })());
  }))));
};

/**
 * Flattens a nested object's keys, camelCasing the resulting keys.
 * E.g. `{ foo: { bar: { baz: 'foo' } } } => { fooBarBaz: 'foo' }`
 *
 * @memberof module:EventTracker
 * @private
 *
 * @param {Object} object
 * @param {String} [prefix] Used in recursion.
 * @return {Object}
 */
var camelFlattenKeys = function camelFlattenKeys(object) {
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

  var toCamel = function toCamel(str) {
    return str.slice(0, 1).toUpperCase() + str.slice(1);
  };

  return Object.keys(object).reduce(function (result, key) {
    var camelKey = prefix.length ? toCamel(key) : key;

    return _extends({}, result, _typeof(object[key]) === 'object' ? camelFlattenKeys(object[key], '' + prefix + camelKey) : _defineProperty({}, '' + prefix + camelKey, object[key]));
  }, {});
};

/**
 * Parses the event's "path" (the elements it bubbled through) for the
 * first element that contains a `data` attribute with analytics metadata.
 *
 * @memberof module:EventTracker
 * @private
 *
 * @param {Event} event
 * @return {Object} The analytics data that was found (or empty object).
 */
var parseAnalyticsDataFromEvent = function parseAnalyticsDataFromEvent(event) {
  var path = event.composedPath();
  var element = path.find(function (el) {
    return el.dataset && el.dataset.analyticsEvent;
  });
  return element ? datasetToObject(element.dataset).analytics : {};
};

var csvToArray = function csvToArray(csv) {
  return csv.split(',').map(function (str) {
    return str.trim();
  });
};

// TODO: Workaround for Analytics. We need to allow for DOM events
// to reach the analytics event handlers before navigating.
// https://codepen.io/stiggler/pen/PRggZq
var withTimeout = function withTimeout(fn) {
  return function () {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return new Promise(function (resolve) {
      return setTimeout(function () {
        return resolve(fn.apply(fn, args));
      });
    });
  };
};

var U = function U() {
  return undefined;
};

/**
 * Returns value at path if none of it's values are `undefined` or `null`;
 * otherwise returns `undefined`.
 *
 * @example
 *
 * ```js
 * selector({}, 'user')
 * // undefined
 *
 * selector({ user: "Foo Bar" }, "user")
 * // Foo Bar
 *
 * selector({ user: "Foo Bar" }, "user", val => val.toUpperCase())
 * // FOO BAR
 *
 * const data = {
 *   user: {
 *     name: "Roger",
 *     email: "r@g.com",
 *     move: { id: "1234", date: Date.now() }
 *   }
 * };
 * selector(data, "user", user => ({
 *   moveId: selector(user, "move.id")
 * }))
 * // { moveId: '1234' }
 * ```
 *
 * @memberof module:EventTracker
 *
 * @param {object} obj Source object
 * @param {string} path Path to property
 * @param {function} modifier Modifier function
 * @returns {*|undefined}
 */
var selector = function selector(obj, path) {
  var modifier = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : identity;
  return obj ? modifier(view(lensPath(path.split('.')), obj)) : undefined;
};

var isEmpty = function isEmpty(obj) {
  return Object.keys(obj).length === 0 || Object.values(obj).every(function (val) {
    return isUndefined(val) || val === '';
  });
};

var clean = function clean(obj) {
  return map(cond([[isObject, ifElse(isEmpty, U, clean)], [isString, when(function (val) {
    return val === '';
  }, U)], [T, identity]]), obj);
};

/**
 * Converts a front end event into an event stream event.
 *
 * @memberof module:EventTracker
 *
 * @param {object} event Front end formatted event
 * @returns {object} Event stream formatted event
 */
var updaterEventFromEvent = function updaterEventFromEvent(event) {
  return clean({
    timestamp: event.timestamp,
    clientSessionId: event.clientSessionId,
    domain: snakeCase(event.domain),
    object: snakeCase(event.object),
    verb: snakeCase(event.verb),
    user: event.user,
    page: event.page,
    details: event.details
  });
};

var warn = curry(function (env, message) {
  if (['development', 'test'].includes(env.name)) console.warn(message); // eslint-disable-line no-console
});

/* eslint-disable arrow-body-style */
var flatten = function flatten() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref3$delimiter = _ref3.delimiter,
      delimiter = _ref3$delimiter === undefined ? '.' : _ref3$delimiter;

  return function recurse(source, path) {
    if (isUndefined(source)) {
      return {};
    }

    if (!isObject(source) || !Object.keys(source).length) return _defineProperty({}, path, source);

    return Object.keys(source).reduce(function (obj, key) {
      var value = source[key];
      if (Array.isArray(value)) return obj;

      var deeperPath = path ? path + delimiter + key : key;
      return Object.assign(obj, recurse(value, deeperPath));
    }, {});
  };
};
/* eslint-enable */

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var attributes = {};
var plugins = [];
var environment = { name: '' };

/**
 * @description
 * `setAttributes` takes a function and updates `attributes` with the results
 * of that function. `attributes` can be functions in addition to strings, numbers, or
 * booleans.
 *
 * @example
 * import { setAttributes } from '@updater/ui-event-tracker';
 *
 * setAttributes(attributes => ({
 *   ...attributes,
 *   page: () => ({
 *     app: "example",
 *     hello: "world"
 *   })
 * }));
 *
 * @param {function} fn Should return new `attributes` given existing `attributes`.
 * @returns {undefined}
 */
var setAttributes = function setAttributes(fn) {
  attributes = fn(attributes);
};

/**
 * @description
 * `setPlugins` takes a function and updates `plugins` with the results
 * of that function. A plugin should be an object that satisfies the
 * following interface:
 *
 * | Key | Description |
 * | --- | --- |
 * | track | Function called for all events. |
 *
 * @example
 * import { setPlugins } from '@updater/ui-event-tracker';
 *
 * setPlugins(plugins => [
 *   ...plugins,
 *   {
 *     track: event => console.log(event)
 *   },
 * ]);
 *
 * @param {function} fn Should return new `plugins` given existing `plugins`.
 * @returns {undefined}
 */
var setPlugins = function setPlugins(fn) {
  plugins = fn(plugins);
};

var resolve = when(isObject, function (data) {
  return map(function (d) {
    return resolve(when(isFunction, function (val) {
      return val();
    })(d));
  }, data);
});

var getAttributes = function getAttributes() {
  return resolve(attributes);
};

var setEnvironment = function setEnvironment(e) {
  environment = e;
};

/**
 * @description
 * Track function to be used for dispatching all user interface events.
 *
 * @example
 * import { track } from '@updater/ui-event-tracker';
 *
 * // With object
 * track({
 *   verb: DISPLAY,
 *   object: 'promotions_offers',
 *   details: { ... }
 * });
 *
 * // With function
 * track(attributes => ({
 *   verb: DISPLAY,
 *   object: 'promotions_offers',
 *   page: {
 *     ...attributes.page,
 *     name: 'Landing'
 *   },
 *   details: { ... }
 * }));
 *
 * @param {(function|object)} attributes Function should return new `attributes` given existing
 * `attributes`. Object should be new `attributes` that will be merged into existing attributes.
 * @returns {undefined}
 */
var track = function track() {
  for (var _len = arguments.length, attrs = Array(_len), _key = 0; _key < _len; _key++) {
    attrs[_key] = arguments[_key];
  }

  var resolvedAttributes = [attributes].concat(attrs).map(function (val) {
    return isFunction(val) ? val(resolve(attributes)) : val;
  }).map(resolve).reduce(function (acc, val) {
    return mergeDeepRight(acc, val);
  }, {});

  var warnWithEnv = warn(environment);

  if (resolvedAttributes.action) {
    warnWithEnv('[EVENT] - `action` is deprecated. Please use `verb`. ' + JSON.stringify(resolvedAttributes));
    resolvedAttributes.verb = resolvedAttributes.action;
    delete resolvedAttributes.action;
  }

  if (resolvedAttributes.subject) {
    warnWithEnv('[EVENT] - `subject` is deprecated. Please use `object`. ' + JSON.stringify(resolvedAttributes));
    resolvedAttributes.object = resolvedAttributes.subject;
    delete resolvedAttributes.subject;
  }

  if (resolvedAttributes.payload) {
    warnWithEnv('[EVENT] - `payload` is deprecated. Please use `details`. ' + JSON.stringify(resolvedAttributes));
    resolvedAttributes.details = mergeDeepRight(resolvedAttributes.details, resolvedAttributes.payload);
    delete resolvedAttributes.payload;
  }

  if (snakeCase(resolvedAttributes.domain) !== resolvedAttributes.domain) {
    warnWithEnv('[EVENT] - `domain` should be snakecased. ' + JSON.stringify(resolvedAttributes));
  }

  if (snakeCase(resolvedAttributes.object) !== resolvedAttributes.object) {
    warnWithEnv('[EVENT] - `object` should be snakecased. ' + JSON.stringify(resolvedAttributes));
  }

  if (snakeCase(resolvedAttributes.verb) !== resolvedAttributes.verb) {
    warnWithEnv('[EVENT] - `verb` should be snakecased. ' + JSON.stringify(resolvedAttributes));
  }

  return Promise.all(plugins.map(function (plugin) {
    return plugin.track(resolvedAttributes);
  }));
};

/**
 * @description
 * Add track listener to a top level element. Listen to DOM events and call `track`
 * with properties extracted from the DOM event's target element. **This listener does
 * not stop events so it's meant to be applied to one top level element only. Be very
 * careful when using this more than once in an application.**
 *
 * Keep in mind that attributes returned by the attribute parameter of `trackOn` will
 * be overridden by those derived from attributes on the target DOM element.
 *
 * @example
 * import { trackOn } from '@updater/ui-event-tracker';
 *
 * const element = document.querySelector('#root');
 *
 * const trackOff = trackOn(element, attributes => ({
 *   domain: 'Promotions',
 *   page: {
 *     ...attributes.page,
 *     name: () => { ... }
 *   }
 * });
 *
 * ...
 *
 * trackOff();
 *
 * @param {HTMLElement} element `element` to attach listener to.
 * @param {object | function} attributes `attributes` to attach to every track event.
 * @returns {function} Removes listeners from `element`.
 */
var trackOn = function trackOn(element, attrs) {
  var eventListeners = Object.keys(TRACKED_EVENTS).map(function (eventType) {
    return {
      eventType: eventType,
      listener: function listener(domEvent) {
        var _parseAnalyticsDataFr = parseAnalyticsDataFromEvent(domEvent),
            event = _parseAnalyticsDataFr.event,
            properties = _parseAnalyticsDataFr.properties,
            on = _parseAnalyticsDataFr.on,
            eventstream = _parseAnalyticsDataFr.eventstream;

        var detail = TRACKED_EVENTS[eventType].detail && domEvent.detail;

        if (event && (!on || csvToArray(on).indexOf(eventType) !== -1)) {
          var verb = TRACKED_EVENTS[eventType].verb;

          var details = _extends$1({}, eventstream || {}, camelFlattenKeys(properties || {}), detail && !eventstream ? { value: detail } : {});

          var resolvedAttributes = when(isFunction, function (fn) {
            return fn(resolve(attributes));
          })(attrs);

          var warnWithEnv = warn(environment);

          if (resolvedAttributes.payload) warnWithEnv('[EVENT] - `payload` is deprecated. Please use `details`. ' + JSON.stringify(resolvedAttributes));

          resolvedAttributes = mergeDeepRight(resolvedAttributes, {
            verb: verb,
            object: event,
            details: mergeDeepRight(resolvedAttributes.details || resolvedAttributes.payload, details)
          });

          if (snakeCase(resolvedAttributes.domain) !== resolvedAttributes.domain) {
            warnWithEnv('[EVENT] - `domain` should be snakecased. ' + JSON.stringify(resolvedAttributes));
          }

          if (snakeCase(resolvedAttributes.object) !== resolvedAttributes.object) {
            warnWithEnv('[EVENT] - `object` should be snakecased. ' + JSON.stringify(resolvedAttributes));
          }

          if (snakeCase(resolvedAttributes.verb) !== resolvedAttributes.verb) {
            warnWithEnv('[EVENT] - `verb` should be snakecased. ' + JSON.stringify(resolvedAttributes));
          }

          track(resolve(resolvedAttributes));
        }
      }
    };
  });

  eventListeners.forEach(function (_ref) {
    var eventType = _ref.eventType,
        listener = _ref.listener;
    return element.addEventListener(eventType, listener);
  });

  return function () {
    return eventListeners.forEach(function (_ref2) {
      var eventType = _ref2.eventType,
          listener = _ref2.listener;
      return element.removeEventListener(eventType, listener);
    });
  };
};

var format = function format(str) {
  return snakeCase(str);
};

/**
 * @memberof module:EventTracker
 *
 * @description
 * Logs event to the console.
 *
 * Usage:
 *
 * ```
 * import { logger } from '@updater/ui-event-tracker';
 *
 * setPlugins(plugins => [
 *   ...plugins,
 *   logger()
 * ]);
 * ```
 *
 * @returns {plugin} plugin
 */
var logger = function logger() {
  return {
    track: function track(event) {
      var domain = format(event.domain || '');
      var object = format(event.object || '');
      var verb = format(event.verb || '');

      try {
        /* eslint-disable no-console */
        console.log('%c[EVENT] - ' + domain + '.' + object + '.' + verb + ' -', 'color: #AA00FF', event);
        /* eslint-enable */
      } catch (e) {} // eslint-disable-line no-empty
    }
  };
};

var noAnalyticsWarning = function noAnalyticsWarning() {
  console.warn('`analytics` is not on the `window`'); // eslint-disable-line no-console
};

var analytics = {
  track: function track() {
    var _window$analytics;

    window.analytics ? (_window$analytics = window.analytics).track.apply(_window$analytics, arguments) : noAnalyticsWarning();
  },
  page: function page() {
    var _window$analytics2;

    window.analytics ? (_window$analytics2 = window.analytics).page.apply(_window$analytics2, arguments) : noAnalyticsWarning();
  }
};

var errors = {
  captureMessage: function captureMessage(message, config) {
    return when(function (Raven) {
      return Raven;
    }, function (Raven) {
      return Raven.captureMessage(message, config);
    })(window.Raven);
  }
};

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _TYPE_VERB_MAP;

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var TYPE_VERB_MAP = (_TYPE_VERB_MAP = {}, _defineProperty$1(_TYPE_VERB_MAP, RECEIVE, 'Received'), _defineProperty$1(_TYPE_VERB_MAP, PAGE, 'Viewed'), _defineProperty$1(_TYPE_VERB_MAP, VIEW, 'Viewed'), _defineProperty$1(_TYPE_VERB_MAP, CHANGE, 'Changed'), _defineProperty$1(_TYPE_VERB_MAP, CLICK, 'Clicked'), _defineProperty$1(_TYPE_VERB_MAP, SELECT, 'Selected'), _defineProperty$1(_TYPE_VERB_MAP, CLOSE, 'Closed'), _defineProperty$1(_TYPE_VERB_MAP, SUBMIT, 'Submitted'), _defineProperty$1(_TYPE_VERB_MAP, DISPLAY, 'Displayed'), _defineProperty$1(_TYPE_VERB_MAP, TRACK, 'Tracked'), _defineProperty$1(_TYPE_VERB_MAP, UPDATE, 'Updated'), _defineProperty$1(_TYPE_VERB_MAP, MEASURE, 'Measured'), _defineProperty$1(_TYPE_VERB_MAP, BOUNCE, 'Bounced'), _defineProperty$1(_TYPE_VERB_MAP, TRY, 'Tried'), _defineProperty$1(_TYPE_VERB_MAP, VERIFY, 'Verified'), _defineProperty$1(_TYPE_VERB_MAP, CREATE, 'Created'), _defineProperty$1(_TYPE_VERB_MAP, SAVE, 'Saved'), _defineProperty$1(_TYPE_VERB_MAP, CONFIRM, 'Confirmed'), _defineProperty$1(_TYPE_VERB_MAP, CANCEL, 'Canceled'), _defineProperty$1(_TYPE_VERB_MAP, DISMISS, 'Dismissed'), _defineProperty$1(_TYPE_VERB_MAP, ENTER, 'Entered'), _defineProperty$1(_TYPE_VERB_MAP, LOAD, 'Loaded'), _defineProperty$1(_TYPE_VERB_MAP, VALIDATE, 'Validated'), _defineProperty$1(_TYPE_VERB_MAP, SENT, 'Sent'), _defineProperty$1(_TYPE_VERB_MAP, RESENT, 'Resent'), _defineProperty$1(_TYPE_VERB_MAP, ADD, 'Added'), _defineProperty$1(_TYPE_VERB_MAP, REMOVE, 'Removed'), _defineProperty$1(_TYPE_VERB_MAP, OPEN, 'Opened'), _defineProperty$1(_TYPE_VERB_MAP, PURCHASE, 'Purchased'), _defineProperty$1(_TYPE_VERB_MAP, DECLINE, 'Declined'), _defineProperty$1(_TYPE_VERB_MAP, ENROLL, 'Enrolled'), _defineProperty$1(_TYPE_VERB_MAP, UPLOAD, 'Uploaded'), _TYPE_VERB_MAP);

var stripKeys = function stripKeys(isLegacy) {
  return function (input) {
    if (isLegacy) {
      var network = input.network,
          updater = input.updater,
          _input$updater = input.updater,
          domain = _input$updater.domain,
          object = _input$updater.object,
          verb = _input$updater.verb,
          viewport = input.viewport,
          legacyEvent = _objectWithoutProperties(input, ['network', 'updater', 'updater', 'viewport']);

      return _extends$2({}, legacyEvent, {
        updater: {
          domain: domain,
          object: object,
          verb: verb
        }
      });
    }
    return input;
  };
};

var _segment = function _segment(_ref) {
  var track = _ref.track,
      page = _ref.page,
      onError = _ref.onError;
  return function () {
    return {
      track: when(function (_ref2) {
        var verb = _ref2.verb;
        return [RECEIVE, PAGE, VIEW, CHANGE, CLICK, SELECT, CLOSE, SUBMIT, DISPLAY, TRACK, UPDATE, MEASURE, BOUNCE, TRY, VERIFY, CREATE, SAVE, CONFIRM, CANCEL, DISMISS, ENTER, LOAD, VALIDATE, SENT, RESENT, ADD, REMOVE, OPEN, PURCHASE, DECLINE, ENROLL, UPLOAD, ENGAGE].includes(verb) && analytics;
      }, ifElse(function (_ref3) {
        var verb = _ref3.verb;
        return verb === PAGE;
      }, function (event) {
        return Promise.race([new Promise(function (resolve) {
          return page(event.segmentEventName ? event.segmentEventName : startCase([event.domain, event.object].join(' ').toLowerCase()), stripKeys(event.legacyEvent)(_extends$2({}, event.details, {
            // moveId is here for backwards compatibility with Segment reasons.
            moveId: selector(event, 'user.move.id'),
            updater: updaterEventFromEvent(event)
          })), resolve);
        }), new Promise(function (_, reject) {
          return setTimeout(function () {
            return reject();
          }, 1000);
        })]).catch(onError);
      }, function (event) {
        return Promise.race([new Promise(function (resolve) {
          return track(event.segmentEventName ? event.segmentEventName : startCase([TYPE_VERB_MAP[event.verb], event.domain, event.object].join(' ').toLowerCase()), stripKeys(event.legacyEvent)(_extends$2({}, event.details, {
            // moveId is here for backwards compatibility with Segment reasons.
            moveId: selector(event, 'user.move.id'),
            updater: updaterEventFromEvent(event)
          })), resolve);
        }), new Promise(function (_, reject) {
          return setTimeout(function () {
            return reject();
          }, 1000);
        })]).catch(onError);
      }))
    };
  };
};

/**
 * @memberof module:EventTracker
 *
 * @description
 * Sends events to Segment.
 *
 * Usage:
 *
 * ```
 * import { segment } from '@updater/ui-event-tracker';
 *
 * setPlugins(plugins => [
 *   ...plugins,
 *   segment()
 * ]);
 * ```
 *
 * @returns {plugin} plugin
 */
var segment = _segment({
  track: analytics.track,
  page: analytics.page,
  onError: function onError(err) {
    return errors.captureMessage(err, { level: 'info' });
  }
});

/**
 * @memberof module:EventTracker
 * @typedef {object} environment
 * @property {string} name
 */

/**
 * @memberof module:EventTracker
 *
 * @description
 * Plugin for Optimizely. Responsible for calling the passed in `track` function
 * on track. The `track` function is passed in to prevent a dependency on
 * `@updater/ui-optimizely`. Refer to the [`@updater/ui-optimizely` documentation](https://github.com/Updater/angular-common/tree/master/packages/optimizely)
 * for documentation on the `track` function.
 *
 * Will automatically forward `event.details.revenueInCents` as a revenue tag to
 * optimizely.
 *
 * Usage:
 *
 * ```
 * import { optimizely } from '@updater/ui-event-tracker';
 *
 * setPlugins(plugins => [
 *   ...plugins,
 *   optimizely(() => ({
 *     env: { name: 'development' },
 *     userId: '1234',
 *     track: () => {}
 *   }))
 * ]);
 * ```
 *
 * @param {function} configFn Function to return object with `env`, `track`, and `userId`.
 * @returns {plugin} plugin
 */
var optimizely = function optimizely(configFn) {
  return {
    track: function track(event) {
      var _configFn = configFn(),
          env = _configFn.env,
          userId = _configFn.userId,
          track = _configFn.track;

      if (VERBS.includes(event.verb)) {
        var tags = {};
        if (event.details && event.details.revenueInCents) {
          tags.revenue = event.details.revenueInCents;
        }
        track(env, userId, event, tags);
      }
    }
  };
};

/**
 * @memberof module:EventTracker
 * @typedef {object} eventStreamConfig
 * @property {string}  url
 * @property {string}  authorization
 * @property {string}  accessToken
 * @property {string}  client
 * @property {number}  uid
 */

/**
 * @memberof module:EventTracker
 *
 * @description
 * Event stream event tracker plugin. Transforms the _front end event_
 * into the _event stream event_ and POSTs it to the event stream endpoint.
 *
 * This plugin will transform a _front end event_:
 *
 * ```
 * {
 *   timestamp: 1543416831103,
 *   page: {
 *     app: 'mover',
 *     environment: 'development',
 *     userAgent:
 *       'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_13_6) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36',
 *     url: 'http://app.dev.updater.com/move-in-promotions/claimed/5',
 *     name: 'Multi Select Move-In Offers',
 *   },
 *   channel: 'Updater',
 *   user: {
 *     email: 'charles.pinnix+test41@updater.com',
 *     uuid: 'cf176727-6911-46c2-bb09-a0eedba6be23',
 *     inviteId: null,
 *     move: { id: '3363532', ownership: 'own' },
 *   },
 *   domain: 'Promotions',
 *   action: 'displayed',
 *   subject: 'Next Steps Cards',
 *   payload: {
 *     optimizely: { experiments: { Multi_Claim_Promotions: 'B' } },
 *     steps: [
 *       'Connect TV & Internet',
 *       'Reserve a moving company',
 *       'Forward mail',
 *     ],
 *   },
 * }
 * ```
 *
 * into an _event stream event_:
 *
 * ```
 * {
 *   timestamp: 1543416831103,
 *   domain: 'Promotions',
 *   object: 'Next Steps Cards',
 *   verb: 'displayed',
 *   user: {
 *     email: 'charles.pinnix+test41@updater.com',
 *     uuid: 'cf176727-6911-46c2-bb09-a0eedba6be23',
 *     move: { id: '3363532', ownership: 'own' },
 *   },
 *   page: {
 *     app: 'mover',
 *     environment: 'development',
 *     userAgent:
 *       'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_13_6) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36',
 *     url: 'http://app.dev.updater.com/move-in-promotions/claimed/5',
 *     name: 'Multi Select Move-In Offers',
 *   },
 *   details: {
 *     optimizely: { experiments: { Multi_Claim_Promotions: 'B' } },
 *     steps: [
 *       'Connect TV & Internet',
 *       'Reserve a moving company',
 *       'Forward mail',
 *     ],
 *   },
 * }
 * ```
 *
 * Usage:
 *
 * ```
 * import { eventStreamPlugin } from '@updater/ui-event-tracker';
 *
 * setPlugins(plugins => [...plugins, eventStreamPlugin(() => ({
 *   url,
 *   app: 'Mover',
 *   authorization,
 *   accessToken,
 *   client,
 *   uid
 * }))]);
 * ```
 *
 * @param {eventStreamConfig} configFn
 * @returns {plugin} plugin
 */
var eventStreamPlugin = function eventStreamPlugin(configFn) {
  return {
    track: function track(event) {
      var _configFn = configFn(),
          url = _configFn.url,
          app = _configFn.app,
          authorization = _configFn.authorization,
          accessToken = _configFn.accessToken,
          client = _configFn.client,
          uid = _configFn.uid;

      if (VERBS.includes(event.verb)) {
        fetch(url, {
          method: 'POST',
          mode: 'cors',
          headers: {
            App: app,
            Authorization: authorization,
            'Site-Origin': window.location.href,
            'Access-token': accessToken,
            'Content-type': 'application/json',
            client: client,
            uid: uid
          },
          redirect: 'follow',
          referrer: 'no-referrer',
          body: JSON.stringify({ updater: updaterEventFromEvent(event) })
        });
      }
    }
  };
};

var _LEVELS;

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var LEVELS = (_LEVELS = {}, _defineProperty$2(_LEVELS, ERROR, 'error'), _defineProperty$2(_LEVELS, WARNING, 'warning'), _LEVELS);

// We pass `captureMessage` in below for testing purposes. The `sentry`
// plugin is the plugin we should normally use.
var plugin = function plugin(captureMessage) {
  return function () {
    return {
      track: when(function (_ref) {
        var verb = _ref.verb;
        return [ERROR, WARNING].includes(verb);
      }, function (event) {
        return captureMessage(event.details.message || event.object, {
          extra: event,
          level: LEVELS[event.verb],
          tags: {
            domain: event.domain
          }
        });
      })
    };
  };
};

/**
 * @memberof module:EventTracker
 *
 * @description
 * Sends errors and warnings to Sentry. This plugin only handles events with
 * verbs `ERROR` and `WARNING`.
 *
 * Usage:
 *
 * ```
 * import { sentry } from '@updater/ui-event-tracker';
 *
 * setPlugins(plugins => [
 *   ...plugins,
 *   sentry()
 * ]);
 * ```
 *
 * @returns {plugin} plugin
 */
var sentry = plugin(errors.captureMessage);

var _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _toConsumableArray$1(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

var storageInterface = function storageInterface(storageKey) {
  return {
    readEvents: function readEvents() {
      return JSON.parse(localStorage.getItem(storageKey)) || [];
    },
    writeEvents: function writeEvents(events) {
      return localStorage.setItem(storageKey, JSON.stringify(events));
    },
    clearEvents: function clearEvents() {
      return localStorage.removeItem(storageKey);
    }
  };
};

var match = function match(a, b) {
  var pickBy = ['domain', 'object', 'verb'];
  return isEqual(pick(a, pickBy), pick(b, pickBy));
};

var updateCombinations = function updateCombinations(combinations, event, state) {
  var newState = state;

  combinations.forEach(function (combination) {
    if (match(combination.start, event)) {
      // If the combination already exists in the state,
      // replace the start event with this newer one
      var index = newState.findIndex(function (stateCombination) {
        return match(stateCombination.start, combination.start) && match(stateCombination.end, combination.end);
      });

      if (index > -1) {
        newState = [].concat(_toConsumableArray$1(newState.slice(0, index)), [_extends$3({}, combination, {
          start: event
        })], _toConsumableArray$1(newState.slice(index + 1)));
      } else {
        newState = [].concat(_toConsumableArray$1(newState), [_extends$3({}, combination, {
          start: event
        })]);
      }
    }
  });

  return newState;
};

var _differencePlugin = function _differencePlugin(_ref) {
  var readEvents = _ref.readEvents,
      writeEvents = _ref.writeEvents,
      clearEvents = _ref.clearEvents;
  return function (_ref2) {
    var _ref2$combinations = _ref2.combinations,
        combinations = _ref2$combinations === undefined ? [] : _ref2$combinations,
        _ref2$persist = _ref2.persist,
        persist = _ref2$persist === undefined ? [] : _ref2$persist,
        _track = _ref2.track;

    var state = [];

    // Read saved events (should be from other application on the same domain) and
    // build state with those events by "playing" them through.
    readEvents().forEach(function (event) {
      state = updateCombinations(combinations, event, state);
    });

    // Clear localStorage so we don't read the same events again.
    clearEvents();

    return {
      track: function track(event) {
        state = updateCombinations(combinations, event, state);

        var matches = state.filter(function (combination) {
          return match(_extends$3({}, combination.end, {
            clientSessionId: combination.start.clientSessionId
          }), event);
        });

        var misses = state.filter(function (combination) {
          return !match(_extends$3({}, combination.end, {
            clientSessionId: combination.start.clientSessionId
          }), event);
        });

        matches.forEach(function (combination) {
          var newCombination = _extends$3({}, combination, {
            end: event
          });
          var newEvent = newCombination.event(newCombination.start, newCombination.end);
          setTimeout(function () {
            return _track(newEvent);
          });
        });

        state = misses;

        persist.forEach(function (e) {
          if (match(e, event)) {
            var events = readEvents();
            var index = events.findIndex(function (e) {
              return match(e, event);
            });

            if (index > -1) {
              events = [].concat(_toConsumableArray$1(events.slice(0, index)), [event], _toConsumableArray$1(events.slice(index + 1)));
            } else {
              events = [].concat(_toConsumableArray$1(events), [event]);
            }

            writeEvents(events);
          }
        });
      }
    };
  };
};

var differencePlugin = _differencePlugin(storageInterface('upd_difference_events'));

var dotFlatten = flatten();

/**
 * @memberof module:EventTracker
 *
 * @description
 * Sends events to New Relic.
 *
 * Usage:
 *
 * ```
 * import { newrelicPlugin } from '@updater/ui-event-tracker';
 *
 * setPlugins(plugins => [
 *   ...plugins,
 *   newrelic(newrelic)
 * ]);
 * ```
 *
 * @param {object} newrelic - New Relic browser SDK
 * @returns {plugin} plugin
 */
var newrelicPlugin = function newrelicPlugin(newrelic) {
  return {
    track: when(function (event) {
      return [MEASURE].includes(event.verb) && newrelic;
    }, function (event) {
      return newrelic.addPageAction([snakeCase(event.domain), snakeCase(event.object), snakeCase(event.verb)].join('.'), dotFlatten({ updater: event }));
    })
  };
};

export { RECEIVE, PAGE, VIEW, CHANGE, CLICK, SELECT, CLOSE, SUBMIT, DISPLAY, TRACK, UPDATE, MEASURE, BOUNCE, ERROR, WARNING, PURCHASE, DECLINE, ENROLL, UPLOAD, ENGAGE, TRY, VERIFY, CREATE, SAVE, CONFIRM, CANCEL, DISMISS, ENTER, LOAD, VALIDATE, SENT, RESENT, ADD, REMOVE, OPEN, VERBS, TRACKED_EVENTS, datasetToObject, camelFlattenKeys, parseAnalyticsDataFromEvent, csvToArray, withTimeout, selector, isEmpty, clean, updaterEventFromEvent, warn, flatten, setAttributes, setPlugins, getAttributes, setEnvironment, track, trackOn, logger, _segment, segment, optimizely, eventStreamPlugin, plugin, sentry, match, _differencePlugin, differencePlugin, newrelicPlugin };
